import {
    Radio,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    type Theme,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useEffect, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useSelector } from 'react-redux';
import Icon from '/@/components/icon';
import useUnitProvider from '/@/core/context/unit';
import { useAllCatalogProducts } from '/@/hooks/catalog';
import useMyRecommendations from '/@/hooks/my-recommendations/useFilterMyRecommendations';
import { useEnvironments } from '/@/hooks/recommendation';
import { _ } from '/@/locale';
import { getCatalogBrand } from '/@/store/catalog/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import type { DensityRecommendationForm } from '/@/types/density-recommendation';
import type { Field } from '/@/types/field';
import {
    getMaxAndMinEnvironmentFromRankings,
    mapPositionChips,
} from '../../../dashboard/rv/components/product-catalog';

const getPositionSortValue = (position: any): number => {
    if (!position || position === '-' || position.label === '-') {
        return -1; // Put products with no position at the end
    }
    if (position.minEnvironment?.value !== undefined) {
        return position.minEnvironment.value;
    }

    return -1;
};

const ProductsStep = ({ form, field }: { form: UseFormReturn<DensityRecommendationForm>; field: Field }) => {
    const units = useUnitProvider();

    const { recos } = useMyRecommendations({
        checkOldRecos: false,
        startFilters: {
            field_id: form.watch('fieldId'),
            type: 'corn',
            page_size: 10000,
            search: '',
        },
    });

    const wasProductRecommended = (product: any) => {
        return recos.some((r) => r.product.some((p) => p.id === product.id));
    };

    const columns = [
        { label: _('Híbrido'), value: (p) => p.name },
        { label: _('Zona'), value: (p) => [...new Set(p.zones.map((z) => z.shortName))].join(', ') },
        {
            label: _('Posicionamiento ideal'),
            value: (p) => p.position,
        },
        {
            label: '',
            value: (p) =>
                wasProductRecommended(p) ? (
                    <Stack direction='row' alignItems='center'>
                        <Icon padding={false} size='sm' type='recommendationCheck' />
                        <Typography variant='body2' color='primary'>
                            {_('Recomendado para el lote')}
                        </Typography>
                    </Stack>
                ) : (
                    ''
                ),
        },
    ];

    const country = useSelector(getSessionCountry);
    const cornBrand = useSelector((state) => getCatalogBrand(state, 'corn'));

    const environmentQuery = useEnvironments({
        lat: field?.lat,
        lng: field?.lng,
        countryCode: country?.code,
        plantingDate: form.watch('plantingDate'),
        crop: 'corn',
        climatePattern: form.watch('climatePattern'),
        soilMoisture: form.watch('soilMoisture'),
    });
    useEffect(() => {
        if (environmentQuery.data?.default) form.setValue('environment', environmentQuery.data.default);
    }, environmentQuery.data?.default);

    const cornProductsQuery = useAllCatalogProducts(
        {
            country: country.code!,
            brand: cornBrand?.name,
            crop: 'corn',
            densityCoefficientsEnvironment: environmentQuery.data?.default,
            zonesPlantingDate: form.watch('plantingDate'),
        },
        { enabled: !!environmentQuery.data?.default && !environmentQuery.isLoading }
    );

    const fieldZones = field?.node?.zones?.filter((z) => z.type === 'corn') || [];

    const cornProducts = useMemo(() => {
        let products = cornProductsQuery.data?.results || [];

        // First, add position data to products
        products = products.map((p: any) => ({
            ...p,
            position: getMaxAndMinEnvironmentFromRankings(p.rankings, p.newRankings, units),
        }));

        // Filter products by field zones
        products = products.filter((p) =>
            fieldZones.some((z) => p.zones.some((z2: any) => z2.codeName === z.codename))
        );

        // Sort products by position BEFORE converting to chips
        products.sort((a: any, b: any) => {
            const aValue = getPositionSortValue(a.position);
            const bValue = getPositionSortValue(b.position);
            return aValue - bValue;
        });

        // Convert to chips after sorting
        products = mapPositionChips(products);

        return products;
    }, [cornProductsQuery.data?.results, fieldZones, units]);

    const watchProductId = form.watch('productId');

    const renderTableRows = (products: any[], columns: { label: string; value: (p: any) => any }[]) => {
        return products.map((product) => (
            <TableRow key={product.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell>
                    <Radio
                        color='secondary'
                        checked={watchProductId === product.id}
                        onChange={() => {
                            form.setValue('productId', product.id);
                        }}
                    />
                </TableCell>
                {columns.map((col) => (
                    <TableCell key={col.label}>
                        <Typography variant='body2' color='primary'>
                            {col.value(product)}
                        </Typography>
                    </TableCell>
                ))}
            </TableRow>
        ));
    };

    const renderSkeletonRows = (columns: { label: string }[]) => {
        const skeletonRows = [];
        for (let i = 0; i < 5; i++) {
            skeletonRows.push(
                <TableRow key={`skeleton-${i}`} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    {columns.map((col) => (
                        <TableCell key={col.label}>
                            <Skeleton variant='text' width='100%' />
                        </TableCell>
                    ))}
                    <TableCell size='small' height='24px'>
                        <Skeleton variant='rounded' width='100%' />
                    </TableCell>
                </TableRow>
            );
        }
        return skeletonRows;
    };

    const isLgOrLower = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

    return (
        <Stack
            sx={{
                background: 'white',
                height: '100%',
            }}
        >
            <Stack sx={{ p: 2 }} spacing={1}>
                <Table
                    size={isLgOrLower ? 'small' : 'medium'}
                    sx={{
                        '& .MuiTableCell-root': {
                            px: 0.5,
                            py: 1,
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            {columns.map((col) => (
                                <TableCell key={col.label} sx={{ fontWeight: 'bold' }}>
                                    <Typography variant='subtitle2' color='primary' fontWeight='bold'>
                                        {col.label}
                                    </Typography>
                                </TableCell>
                            ))}
                            <TableCell size='small' width='24px' />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {renderTableRows(cornProducts, columns)}
                        {cornProductsQuery.isValidating && renderSkeletonRows(columns)}
                    </TableBody>
                </Table>
            </Stack>
        </Stack>
    );
};

export default ProductsStep;
